import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import ModalSignUp, {
  ModalSignUpProps,
} from "domains/auth/components/ModalSignUp";
import usePersistedState, {
  PersistedStateKey,
} from "domains/commons/hooks/usePersistedState";
import { useScenarioToast } from "domains/notification/hooks/useScenarioToast";
import Loading from "domains/ui/components/Loading";
import {
  NOTIFICATION_TYPES,
  NotificationType,
} from "domains/user/constants/Notifications";
import {
  DEPRECATED_NSFW_TYPES,
  NSFW_TYPES,
  NsfwType,
} from "domains/user/constants/Nsfw";
import { PendingInvitation } from "domains/user/interfaces/Invitation";
import { OnboardingForm } from "domains/user/interfaces/OnboardingForm";
import { AnalyticsEvents } from "infra/analytics/constants/Events";
import Track from "infra/analytics/Track";
import { useHandleApiError } from "infra/api/error";
import {
  GetMeApiResponse,
  useGetMeQuery,
  useLazyGetUserConfirmInvitationQuery,
  usePutMeMutation,
} from "infra/api/generated/api";
import { apiSlice } from "infra/store/apiSlice";
import { API_TAGS } from "infra/store/constants";
import store from "infra/store/store";
import _ from "lodash";
import moment from "moment";

import { Center } from "@chakra-ui/react";
import { useUser as useClerkUser } from "@clerk/nextjs";
import { skipToken } from "@reduxjs/toolkit/dist/query";

export type UserContextValue = {
  isUnsigned: boolean;
  user: GetMeApiResponse["user"] | undefined;
  userSettings: { [key: string]: any } | undefined;
  updateUserSettings: (newSettings: { [key: string]: any }) => void;
  featureFlags: string[];

  nsfwRevealedAssetIds: string[];
  nsfwFilteredTypes: NsfwType[];
  setNsfwFilter: (values: NsfwType[]) => void;
  revealAsset: (assetId: string) => void;

  notificationSettings: NotificationType[];
  setNotificationSettings: (settings: NotificationType[]) => void;

  policy: string | undefined;
  lastAcceptedPolicy: string | undefined;
  markPolicyAsRead: () => void;
  isPolicyAcceptanceMissing: boolean;

  submitOnboardingForm: (form: OnboardingForm) => void;
  isOnboardingFormMissing: boolean;

  openSignUpModal: (variant: ModalSignUpProps["variant"]) => void;

  pendingInvitation: PendingInvitation | undefined;
  setPendingInvitation: (invitation: PendingInvitation | undefined) => void;
};

export const UserContext = createContext<UserContextValue>({
  isUnsigned: true,
  user: undefined,
  userSettings: undefined,
  updateUserSettings: () => {},
  featureFlags: [],

  nsfwRevealedAssetIds: [],
  nsfwFilteredTypes: [],
  setNsfwFilter: () => {},
  revealAsset: () => {},

  notificationSettings: [],
  setNotificationSettings: () => {},

  policy: undefined,
  lastAcceptedPolicy: undefined,
  markPolicyAsRead: () => {},
  isPolicyAcceptanceMissing: false,

  submitOnboardingForm: () => {},
  isOnboardingFormMissing: false,

  openSignUpModal: () => {},

  pendingInvitation: undefined,
  setPendingInvitation: () => {},
});

// ------------------------------------

export function UserProvider({ children }: PropsWithChildren) {
  const { user: clerkUser } = useClerkUser();
  const { successToast } = useScenarioToast();
  const handleApiError = useHandleApiError();
  const [nsfwRevealedAssetIds, setNsfwRevealedAssetIds] = useState<string[]>(
    []
  );
  const [signUpModalVariant, setSignUpModalVariant] = useState<
    ModalSignUpProps["variant"] | undefined
  >();
  const [pendingInvitation, setPendingInvitation] = usePersistedState<
    PendingInvitation | undefined
  >(PersistedStateKey.PENDING_INVITATION, {
    defaultValue: undefined,
  });
  const [putMeTrigger] = usePutMeMutation();
  const [confirmInvitationTrigger] = useLazyGetUserConfirmInvitationQuery();

  const { data: { user } = {} } = useGetMeQuery(
    clerkUser ? undefined : skipToken
  );

  const isUnsigned = !(user && clerkUser);

  const featureFlags = useMemo(() => user?.features ?? [], [user?.features]);
  const settings = useMemo(
    ():
      | {
          [key: string]: any;
        }
      | undefined => user?.settings,
    [user?.settings]
  );

  const nsfwFilteredTypes = useMemo<NsfwType[]>(() => {
    if (settings && typeof settings["nsfw-filter"] === "string") {
      const savedTypes = settings["nsfw-filter"].split(",");

      const addRelatedTypeIfMissing = (typeA: string, typeB: string) => {
        if (savedTypes.includes(typeA) && !savedTypes.includes(typeB)) {
          savedTypes.push(typeB);
        }
        if (savedTypes.includes(typeB) && !savedTypes.includes(typeA)) {
          savedTypes.push(typeA);
        }
      };
      addRelatedTypeIfMissing("explicit_nudity", "explicit");
      addRelatedTypeIfMissing("suggestive", "non_explicit");

      return NSFW_TYPES.filter((value) => savedTypes.includes(value));
    } else {
      return ["explicit_nudity", "explicit"];
    }
  }, [settings]);

  const notificationSettings = useMemo(() => {
    const keys = Object.keys(user?.notifications ?? {}) as NotificationType[];
    return keys.reduce((acc, key) => {
      if (
        user?.notifications?.[key]?.email ||
        user?.notifications?.[key]?.mobile
      ) {
        acc.push(key as NotificationType);
      }
      return acc;
    }, [] as NotificationType[]);
  }, [user]);

  const policy = featureFlags
    .find((flag) => flag.match(/^policy:.+$/))
    ?.replace(/^policy:/, "");
  const lastAcceptedPolicy =
    policy && settings && (settings["last-policy-accepted"] as string);
  const isPolicyAcceptanceMissing = !!(policy && policy !== lastAcceptedPolicy);
  const isOnboardingFormMissing = !!(settings && !settings["onboarded"]);

  // ----------------------------------

  // https://github.com/scenario-labs/phoenix/blob/6b8e1b16ecc5eda2f1bc605c162149e7b41efe7d/src/lib/types/user.ts#L9
  const updateUserSettings = useCallback(
    async (newSettings: { [key: string]: any }) => {
      if (!user) {
        throw new Error();
      }
      await putMeTrigger({
        body: {
          settings: {
            ...settings,
            ...newSettings,
          },
        },
      }).unwrap();
    },
    [putMeTrigger, settings, user]
  );

  const revealAsset = useCallback(
    (assetId: string) =>
      setNsfwRevealedAssetIds((assetIds) => [...assetIds, assetId]),
    [setNsfwRevealedAssetIds]
  );

  const setNsfwFilter = useCallback(
    async (values: NsfwType[]) => {
      try {
        await updateUserSettings({
          "nsfw-filter": values
            .filter((item) => !DEPRECATED_NSFW_TYPES.includes(item))
            .join(","),
        });
      } catch (error) {
        handleApiError(
          error,
          "There was an error saving your safety filter preference"
        );
      }
    },
    [handleApiError, updateUserSettings]
  );

  const setNotificationSettings = useCallback(
    async (newNotificationSettings: NotificationType[]) => {
      try {
        await putMeTrigger({
          body: {
            notifications: {
              ...NOTIFICATION_TYPES.reduce((acc, key) => {
                acc[key] = {
                  email: newNotificationSettings.includes(key),
                  mobile: newNotificationSettings.includes(key),
                };
                return acc;
              }, {} as NonNullable<GetMeApiResponse["user"]["notifications"]>),
            },
          },
        });
      } catch (error) {
        handleApiError(
          error,
          "There was an error saving your notification settings"
        );
      }
    },
    [handleApiError, putMeTrigger]
  );

  const markPolicyAsRead = useCallback(async () => {
    try {
      await updateUserSettings({
        "last-policy-accepted": policy,
      });
      Track(AnalyticsEvents.User.PolicyAccepted, { policy });
    } catch (error) {
      handleApiError(error, "Error saving policy opt-in");
    }
  }, [handleApiError, policy, updateUserSettings]);

  const submitOnboardingForm = useCallback(
    async (form: OnboardingForm) => {
      try {
        await putMeTrigger({
          body: {
            fullName: form.fullName,

            ...(() => {
              if (form.phoneNumber && form.countryCode) {
                return {
                  phoneNumber: form.phoneNumber,
                  countryCode: form.countryCode.toUpperCase(),
                };
              } else {
                return {};
              }
            })(),
          },
        }).unwrap();

        await updateUserSettings({
          onboarded: true,
          "company-name": form.companyName,
          role: form.role,
          "traffic-source": form.source,
          "platform-usage": form.usage,
          contact: form.contact,
        });

        successToast({ title: "Profile completed" });

        Track(
          AnalyticsEvents.User.Onboarded,
          _.omit(form, ["fullName", "companyName", "phoneNumber"])
        );
      } catch (error) {
        handleApiError(error, "Error submitting form");
      }
    },
    [successToast, handleApiError, putMeTrigger, updateUserSettings]
  );

  const openSignUpModal = useCallback(
    (variant: ModalSignUpProps["variant"]) => {
      setSignUpModalVariant(variant);
    },
    [setSignUpModalVariant]
  );

  // ----------------------------------

  const handleSignUpModalClose = useCallback(() => {
    setSignUpModalVariant(undefined);
  }, [setSignUpModalVariant]);

  // ----------------------------------

  // Add policy to user settings when account is created
  useEffect(() => {
    if (!policy || lastAcceptedPolicy) return;
    void updateUserSettings({
      "last-policy-accepted": policy,
    });
  }, [policy, lastAcceptedPolicy, updateUserSettings]);

  useEffect(() => {
    if (!pendingInvitation || !user?.email) return;

    const acceptInvitation = async () => {
      if (moment.utc().isAfter(moment.utc(pendingInvitation.expiresAt))) {
        setPendingInvitation(undefined);
        return;
      }

      if (pendingInvitation.email !== user?.email) {
        return;
      }

      try {
        await confirmInvitationTrigger({
          hash: pendingInvitation.hash,
        }).unwrap();
        successToast({
          title: "You've successfully joined an organization.",
        });
        store.dispatch(
          apiSlice.util.invalidateTags([
            {
              type: API_TAGS.team,
            },
          ])
        );
      } catch (error) {
        handleApiError(error, "There was an error accepting the invitation");
      } finally {
        setPendingInvitation(undefined);
      }
    };

    void acceptInvitation();
  }, [
    confirmInvitationTrigger,
    handleApiError,
    pendingInvitation,
    setPendingInvitation,
    successToast,
    user?.email,
  ]);

  // ----------------------------------

  const contextValue = useMemo(
    () => ({
      isUnsigned,
      user:
        user && clerkUser
          ? {
              ...user,
              // not sure why but we always used the fullName from clerkUser
              // if we change if to our user it might change for some people
              // so we keep it as is for now
              fullName: clerkUser.fullName ?? undefined,
            }
          : undefined,
      userSettings: settings,
      updateUserSettings,
      featureFlags,

      nsfwRevealedAssetIds,
      setNsfwRevealedAssetIds,
      nsfwFilteredTypes,
      setNsfwFilter,
      revealAsset,

      notificationSettings,
      setNotificationSettings,

      policy,
      lastAcceptedPolicy,
      markPolicyAsRead,
      isPolicyAcceptanceMissing,

      submitOnboardingForm,
      isOnboardingFormMissing,

      openSignUpModal,

      pendingInvitation,
      setPendingInvitation,
    }),
    [
      isUnsigned,
      user,
      clerkUser,
      settings,
      updateUserSettings,
      featureFlags,
      nsfwRevealedAssetIds,
      nsfwFilteredTypes,
      setNsfwFilter,
      revealAsset,
      notificationSettings,
      setNotificationSettings,
      policy,
      lastAcceptedPolicy,
      markPolicyAsRead,
      isPolicyAcceptanceMissing,
      submitOnboardingForm,
      isOnboardingFormMissing,
      openSignUpModal,
      pendingInvitation,
      setPendingInvitation,
    ]
  );

  if (!!clerkUser && !user) {
    return (
      <Center h="100vh">
        <Loading />
      </Center>
    );
  }

  return (
    <UserContext.Provider value={contextValue}>
      {children}

      {isUnsigned && (
        <ModalSignUp
          variant={signUpModalVariant}
          onClose={handleSignUpModalClose}
        />
      )}
    </UserContext.Provider>
  );
}

export function useUserContext() {
  return useContext<UserContextValue>(UserContext);
}
